/**
 * THE ACCOUNT AVATAR — the one place a profile picture is drawn.
 *
 * Settings drew this inline: a 40px circle of accentSoft with two initials in it,
 * marked aria-hidden because the name it abbreviated was right beside it. That was
 * the only avatar in the app, so "update the picture everywhere the avatar appears"
 * had exactly one site — but a picture that can be uploaded will not stay at one
 * site, and the second copy of a circle-with-initials is where the two start
 * disagreeing about the fallback, the size or the shape. So it is a component now,
 * and it reads the store itself rather than taking a name prop: a surface that has
 * to fetch the profile before it can show the face is a surface that will show a
 * stale one.
 *
 * SAME PIXELS as the inline version at its default size — 40px, `borderRadius: 50%`,
 * `c.accentSoft` behind `c.accent` initials at 13px/600. `size` scales all of it
 * proportionally for a larger presentation, which the upload dialog uses. The
 * initials themselves come from lib/initials.ts, which is where that function went
 * when it left Settings.tsx.
 *
 * THE PICTURE IS A `<img>`, NOT A BACKGROUND IMAGE, so `object-fit: cover` crops a
 * non-square photo to the circle instead of squashing it, and so a broken source
 * fails visibly rather than leaving an empty ring.
 *
 * ARIA. `decorative` (the default) keeps the aria-hidden the Settings header had,
 * because the account name is rendered immediately beside it and announcing "GE"
 * first is noise. A surface without that adjacent name passes `decorative={false}`
 * and gets a real label. Neither choice changes a pixel.
 */
import type { CSSProperties } from 'react';
import { initials } from '../../lib/initials';
import { selectAvatarUrl, selectProfileName, useAuthStore } from '../../stores/authStore';
import { c } from '../../styles/theme';

/**
 * The size the Settings header has always drawn, and this component's default.
 *
 * A constant rather than a bare 40 in the signature, because the badge Settings draws
 * over the circle is positioned against it — two places holding the same number is
 * how a 16px camera badge ends up floating off a 48px avatar.
 */
const AVATAR_SIZE = 40;

export interface AvatarProps {
  /** Diameter in px. Everything inside scales with it. */
  size?: number;
  /**
   * True (the default) marks the circle aria-hidden, for the case where the account
   * name is rendered beside it. False gives it a real accessible name.
   */
  decorative?: boolean;
  /**
   * Shows this image instead of the saved one, without touching the store.
   *
   * The upload dialog's preview: it has to show what WOULD be saved next to what is
   * saved now, and the only honest way to do that is to hand the candidate in rather
   * than writing it somewhere and hoping to undo it if the user cancels.
   */
  previewUrl?: string | null;
  style?: CSSProperties;
}

export default function Avatar({
  size = AVATAR_SIZE,
  decorative = true,
  previewUrl,
  style,
}: AvatarProps) {
  // One field per selector: Zustand v5 compares with Object.is, so an object literal
  // would re-render every avatar on any unrelated auth change.
  const name = useAuthStore(selectProfileName);
  const savedUrl = useAuthStore(selectAvatarUrl);

  // `previewUrl === undefined` means "not previewing"; `null` means "previewing the
  // removal", which must beat the saved picture rather than falling back to it.
  const shown = previewUrl === undefined ? savedUrl : previewUrl;

  /*
    The accessible name, with a branch for an account that has no name.

    `selectProfileName` returns '' for a signed-out read rather than substituting the demo
    account's name (Batch 7 §5), so `name` can be empty — and an `aria-label=""` is not a
    label at all: a screen reader falls back to announcing the element as an unnamed image,
    which is worse than the generic wording below. Unreachable inside `RequireAuth`, and
    handled anyway because the cost is one branch.
  */
  const label = name
    ? shown
      ? `${name}'s profile picture`
      : name
    : shown
      ? 'Profile picture'
      : 'Account';

  const box: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    background: c.accentSoft,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Crops the photo to the circle rather than letting it square off the corners.
    overflow: 'hidden',
    flexShrink: 0,
    ...style,
  };

  // One aria decision for both branches, so a picture and its initials fallback are
  // announced (or silenced) identically — a screen reader should not be able to tell
  // which one is on screen.
  const aria = decorative
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'img', 'aria-label': label } as const);

  if (shown) {
    return (
      <span {...aria} style={box}>
        <img
          src={shown}
          // Empty alt always: the <span> above carries the label when there is one,
          // and duplicating it here would announce the account twice.
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </span>
    );
  }

  return (
    <span
      {...aria}
      style={{
        ...box,
        // Scaled from the 40px/13px pair the Settings header used, so the initials
        // keep their proportion at any size instead of rattling inside a big circle.
        fontWeight: 600,
        fontSize: Math.round(size * 0.325),
        color: c.accent,
      }}
    >
      {initials(name)}
    </span>
  );
}
